<template>
    <section class="projects-section card">
        <div class="section-header">
            <h2>{{ $trans('web_profile.projects') }}</h2>
            <EditButton :onClick="goToEmployment" />
        </div>

        <div v-loading="loading" class="min-h-[200px]">
            <EasyDataTable
                v-if="!loading && localProjects.length"
                :headers="projectHeaders"
                :items="localProjects"
                table-class-name="custom-projects-table"
                hide-footer
                :border-cell="false"
            >
                <template #empty-message>
                    <div style="text-align: center; padding: 20px; color: #888">
                        {{ $trans('web_profile.no_projects_found') }}
                    </div>
                </template>

                <template #header-period>
                    <div class="header-multiline">
                        <span>{{ $trans('web_profile.from_to') }}</span>
                        <span>{{ $trans('web_profile.duration') }}</span>
                    </div>
                </template>

                <template #header-roles>
                    <div class="header-multiline">
                        <span>{{ $trans('web_profile.main_job_role') }}</span>
                        <span>{{ $trans('web_profile.complimentary_job_role') }}</span>
                    </div>
                </template>

                <template #header-sectors>
                    <div class="header-multiline">
                        <span>{{ $trans('web_profile.main_industry_sector') }}</span>
                        <span>{{ $trans('web_profile.detail_industry_sector') }}</span>
                    </div>
                </template>

                <template #header-location>
                    <div class="header-multiline">
                        <span>{{ $trans('web_profile.city') }}</span>
                        <span>{{ $trans('web_profile.country') }}</span>
                    </div>
                </template>

                <template #item-period="{ from, to, duration }">
                    <div class="cell-multiline">
                        <div class="font-bold">{{ formatDate(from) }} - {{ formatDate(to) }}</div>
                        <div class="text-sm" v-if="duration">{{ duration }}</div>
                    </div>
                </template>

                <template #item-roles="{ main_job_role, complimentary_job_role }">
                    <div class="cell-multiline">
                        <div class="font-bold">{{ formatList(main_job_role) }}</div>
                        <div class="text-sm" v-if="checkIfValid(complimentary_job_role)">
                            {{ formatList(complimentary_job_role) }}
                        </div>
                    </div>
                </template>

                <template #item-sectors="{ main_industry_sector, detail_industry_sector }">
                    <div class="cell-multiline">
                        <div class="font-bold">{{ formatList(main_industry_sector) }}</div>
                        <div class="text-sm" v-if="checkIfValid(detail_industry_sector)">
                            {{ formatList(detail_industry_sector) }}
                        </div>
                    </div>
                </template>

                <template #item-location="{ city, country }">
                    <div class="cell-multiline">
                        <div class="font-bold">{{ city || '-' }}</div>
                        <div>{{ country || '-' }}</div>
                    </div>
                </template>
            </EasyDataTable>
        </div>
    </section>
</template>

<script>
import { mapStores } from "pinia";
import { useWebProfileStore } from "@/stores/WebProfileStore";
import EditButton from "./../Components/EditButton.vue";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";

export default {
    name: "ProjectsSection",
    components: { EasyDataTable, EditButton },
    data() {
        return {
            loading: true,
            localProjects: [],
            projectHeaders: [
                { text: "", value: "period" },
                { text: "", value: "roles" },
                { text: "", value: "sectors" },
                { text: "", value: "location" },
            ],
        };
    },
    computed: {
        ...mapStores(useWebProfileStore),
    },
    watch: {
        "WebProfileStore.projects": {
            handler(newProjects) {
                if (!newProjects) return;
                this.mapProjects(newProjects);
            },
            immediate: true,
        },
    },
    methods: {
        mapProjects(rawProjects) {
            let processedProjects = [...rawProjects];

            processedProjects.sort((a, b) => {
                const dateA = a.to ? new Date(a.to) : new Date();
                const dateB = b.to ? new Date(b.to) : new Date();
                return dateB - dateA;
            });

            this.localProjects = processedProjects.map((proj) => ({
                id: proj.id,
                from: proj.from,
                to: proj.to,
                duration: proj.duration,
                main_job_role: proj.main_job_role,
                complimentary_job_role: proj.complimentary_job_role,
                main_industry_sector: proj.main_industry_sector,
                detail_industry_sector: proj.detail_industry_sector,
                city: proj.city,
                country: proj.country,
            }));
            this.loading = false;
        },
        formatList(list) {
            return Array.isArray(list) && list.length ? list.join(", ") : "-";
        },
        checkIfValid(val) {
            if (Array.isArray(val) && val.length === 0) return false;
            if (!val || val === "-") return false;
            return true;
        },
        formatDate(dateString) {
            if (!dateString) return this.$trans('web_profile.present');
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${String(date.getMonth() + 1).padStart(2,"0")}/${date.getFullYear()}`;
        },
        goToEmployment() {
            const id = this.WebProfileStore.profile.id;
            if (!id) return;
            window.open(`/candidates/${id}/employment`, '_blank');
        },
    },
};
</script>

<style scoped>
.projects-section {
    padding: 1rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: #fff;
}

.cell-multiline, .header-multiline {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cell-multiline {
    padding: 8px 0;
}

.sub-header {
    font-weight: 400;
    font-size: 0.9em;
    color: #555;
}

:deep(.custom-projects-table) {
    --easy-table-border: none;
    --easy-table-row-border: none;
    --easy-table-header-font-size: 13px;
    --easy-table-body-row-font-size: 13px;
    --easy-table-header-background-color: transparent;
    --easy-table-body-row-background-color: transparent;
    --easy-table-body-row-font-color: #000;
}

:deep(.vue3-easy-data-table__main),
:deep(.vue3-easy-data-table__body tr) {
    background-color: transparent !important;
}

:deep(.vue3-easy-data-table__main tr td) {
    background-color: #eaf3f7 !important;
    border: 4.5px solid #fff !important;
    border-radius: 4px;
    font-weight: 700 !important;
    color: #000 !important;
    font-size: 16px !important;
}

:deep(.vue3-easy-data-table__main tr th) {
    background-color: transparent !important;
    border: none !important;
    pointer-events: none;
    padding-left: 10px;
    vertical-align: bottom;
    padding-bottom: 10px;
    font-weight: 700 !important;
    color: #000 !important;
    font-size: 16px !important;
}

:deep(.vue3-easy-data-table__body tr:hover td) {
    background-color: #eaf3f7 !important;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

h3 {
    margin: 0;
    font-size: 1.875rem;
    font-weight: 600;
}
</style>
